import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config.json";
import "./addUser.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import StarIcon from "../../components/starIcon/StarIcon";
import TitleBar from "../../components/navbar/TitleBar";
import NavMenu from "../../components/navbar/NavMenu";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import SubTitle from "../../components/common/SubTitle";
import { useTranslation } from "react-i18next";
const AddUser = () => {
  const location = useLocation();
  const { id } = location.state || {};
  const Navigate = useNavigate();
  const { userId } = useParams();
  const path = id || userId ? "users > Update User" : "users > Create User";
  const { t } = useTranslation();

  const [userData, setUserData] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    userName: "",
    password: "",
    roleId: "",
  });

  const [pass, setPass] = useState({
    newPassword: "",
    confirmPassword: "",
  });



  // Styles
  const textFieldProps = {
    fullWidth: true,
    variant: "outlined",
    InputProps: {
      style: {
        borderRadius: "16px",
      },
    },
    sx: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderWidth: "2px",
          borderColor: "rgba(24, 48, 89, 1)",
        },
      },
    },
  };

  const selectStyles = {
    borderRadius: "16px",
    height: "56px",
    border: "2px solid rgba(24, 48, 89, 1)",
  };

  // Fetch user data and roles
  useEffect(() => {
    if (id) {
      setUpdateMode(true);
      // Fetch user details
      axios
        .get(`${config.baseURL}/user/${id}`, { withCredentials: true })
        .then((response) => {
          if (response && response.data.user) {
            const user = response.data.user;
            const nameParts = user.name.split(" ");
            setFormData({
              firstName: nameParts[0] || "",
              lastName: nameParts.slice(1).join(" ") || "",
              email: user.email,
              userName: user.userName,
              roleId: user.roleId,
            });
          }
        })
        .catch((error) => console.error("Error fetching user data:", error));
    }

    // Fetch roles
    axios
      .get(`${config.baseURL}/role/organization`, { withCredentials: true })
      .then((res) => {
        setAllRoles(res.data.roles);
      })
      .catch((error) => {
        console.log("Error in fetching roles", error);
      });
  }, [id]);

  // Event Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePassChange = (e) => {
    const { name, value } = e.target;
    setPass((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (e) => {
    const selectedRoleName = e.target.value;
    const selectedRole = allRoles.find(
      (role) => role.name === selectedRoleName
    );

    if (selectedRole) {
      setFormData((prev) => ({
        ...prev,
        roleId: selectedRole.id,
      }));
    }
  };

  const togglePasswordFields = () => {
    setShowPasswordFields(!showPasswordFields);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    try {
      if (id) {
        // Update existing user
        await axios.put(
          `${config.baseURL}/user/`,
          {
            userId: id,
            name: fullName,
            email: formData.email,
            roleId: formData.roleId,
          },
          { withCredentials: true }
        );

        // Update password if new password is provided
        if (pass.newPassword && pass.confirmPassword === pass.newPassword) {
          await axios.patch(
            `${config.baseURL}/user/password`,
            {
              userId: id,
              newPassword: pass.confirmPassword,
            },
            { withCredentials: true }
          );
        }
      } else {
        // Create new user
        await axios.post(
          `${config.baseURL}/user/`,
          {
            name: fullName,
            email: formData.email,
            password: formData.password,
            userName: formData.userName,
            organizationId: 1,
            roleId: formData.roleId,
          },
          { withCredentials: true }
        );
      }

      Navigate("/user");
    } catch (error) {
      console.error("Error in user operation:", error);
    }
  };

  const handleCancel = () => {
    Navigate("/user");
  };

  const findRoleNameById = (roleId) => {
    const role = allRoles.find((role) => role.id === roleId);
    return role ? role.name : "";
  };

  return (
    <>
      <TitleBar />
      <NavMenu />
      <SubTitle title="users" path={path} />
      <div style={{ padding: " 0px 30px 15px 30px" }}>
        <Box component="form" autoComplete="off">
          <Grid container spacing={1}>
            {/* First Name and Last Name */}
            <Grid item xs={12} sm={4}>
              <InputLabel className="input-label">
                {t("firstName")} <StarIcon />
              </InputLabel>
              <TextField
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder={t("Type First Name")}
                {...textFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InputLabel className="input-label">
                {t("lastName")} <StarIcon />
              </InputLabel>
              <TextField
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder={t("Type Last Name")}
                {...textFieldProps}
              />
            </Grid>

            {/* Email and Username */}
            {!updateMode && (
              <>
                <Grid item xs={12} sm={4}>
                  <InputLabel className="input-label">
                    {t("email")} <StarIcon />
                  </InputLabel>
                  <TextField
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("Type Email Address")}
                    {...textFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel className="input-label">
                    {t("username")} <StarIcon />
                  </InputLabel>
                  <TextField
                    name="userName"
                    value={formData.userName}
                    onChange={handleChange}
                    placeholder={t("Type Username")}
                    {...textFieldProps}
                  />
                </Grid>
              </>
            )}

            {/* Password and Confirm Password */}
            {!updateMode && (
              <Grid item xs={12} sm={4}>
                <InputLabel className="input-label">
                  {t("password")} <StarIcon />
                </InputLabel>
                <TextField
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t("Type Password")}
                  {...textFieldProps}
                />
              </Grid>
            )}

            {/* Email (Update Mode) and Role */}
            <Grid item xs={12} sm={4}>
              {updateMode ? (
                <>
                  <InputLabel className="input-label">
                    {t("email")} <StarIcon />
                  </InputLabel>
                  <TextField
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("Type Email Address")}
                    {...textFieldProps}
                  />
                </>
              ) : (
                <>
                  <InputLabel className="input-label">
                    {t("role")} <StarIcon />
                  </InputLabel>
                  <FormControl fullWidth>
                    <Select
                      value={findRoleNameById(formData.roleId) || ""}
                      onChange={handleRoleChange}
                      displayEmpty
                      style={selectStyles}
                    >
                      <MenuItem value="" disabled>
                        {t("Select Role")}
                      </MenuItem>
                      {allRoles.map((role) => (
                        <MenuItem key={role.id} value={role.name}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Grid>

            {/* Role (Update Mode) */}
            {updateMode && (
              <Grid item xs={12} sm={6}>
                <InputLabel className="input-label">
                  {t("role")} <StarIcon />
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    value={findRoleNameById(formData.roleId) || ""}
                    onChange={handleRoleChange}
                    displayEmpty
                    style={selectStyles}
                  >
                    <MenuItem value="" disabled>
                      {t("Select Role")}
                    </MenuItem>
                    {allRoles.map((role) => (
                      <MenuItem key={role.id} value={role.name}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Password Reset (Update Mode) */}
            {updateMode && (
              <>
                <Grid item xs={12}>
                  <Button
                    onClick={togglePasswordFields}
                    style={{ textTransform: "none", color: "#20A8E0" }}
                  >
                    {t("Reset Password")}
                    {showPasswordFields ? (
                      <KeyboardDoubleArrowUpIcon />
                    ) : (
                      <KeyboardDoubleArrowDownIcon />
                    )}
                  </Button>
                </Grid>

                {showPasswordFields && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <InputLabel className="input-label">
                        {t("New Password")} <StarIcon />
                      </InputLabel>
                      <TextField
                        name="newPassword"
                        type="password"
                        value={pass.newPassword}
                        onChange={handlePassChange}
                        placeholder={t("Type New Password")}
                        {...textFieldProps}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel className="input-label">
                        {t("Confirm Password")} <StarIcon />
                      </InputLabel>
                      <TextField
                        name="confirmPassword"
                        type="password"
                        value={pass.confirmPassword}
                        onChange={handlePassChange}
                        placeholder={t("Confirm New Password")}
                        {...textFieldProps}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>

          {/* Action Buttons */}
          <Grid
            container
            spacing={3}
            style={{
              marginTop: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCancel}
                style={{
                  borderRadius: "16px",
                  fontWeight: 700,
                  fontSize: "18px",
                  height: "48px",
                  textTransform: "none",
                }}
              >
                {t("Cancel")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                onClick={submitHandler}
                style={{
                  fontStyle: "bold",
                  fontWeight: "700",
                  fontSize: "18px",
                  borderRadius: "16px",
                  background: "#F9E719",
                  height: "48px",
                  textTransform: "none",
                }}
              >
                {t("Submit")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </div>
    </>
  );
};

export default AddUser;

